import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getQuotes, deleteQuote, updateQuoteStatus } from '@/lib/quoteDb';
import Badge from '@/components/base/Badge';
import { quoteStatus } from '@/lib/statusDefs';

const TIME_FILTERS = [
  { key: 'all', label: 'Tümü' },
  { key: 'today', label: 'Bugün' },
  { key: 'week', label: 'Bu Hafta' },
  { key: 'month', label: 'Bu Ay' },
  { key: 'year', label: 'Bu Yıl' },
];

function getDateRange(key: string): { gte?: string; lte?: string } {
  const now = new Date();
  const tr = (d: Date) => d.toISOString().split('T')[0];

  switch (key) {
    case 'today':
      return { gte: tr(now), lte: tr(now) };
    case 'week': {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay() + 1);
      return { gte: tr(start), lte: tr(now) };
    }
    case 'month':
      return { gte: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`, lte: tr(now) };
    case 'year':
      return { gte: `${now.getFullYear()}-01-01`, lte: tr(now) };
    default:
      return {};
  }
}

export default function QuotesPage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<any[]>([]);
  const [filteredQuotes, setFilteredQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await getQuotes();
    if (err) {
      setError(err);
    } else {
      setQuotes(data || []);
      setFilteredQuotes(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  // Apply filters
  useEffect(() => {
    let result = [...quotes];

    // Time filter
    if (timeFilter !== 'all') {
      const range = getDateRange(timeFilter);
      if (range.gte) {
        result = result.filter(q => q.quote_date >= range.gte!);
      }
      if (range.lte) {
        result = result.filter(q => q.quote_date <= range.lte!);
      }
    }

    // Status filter
    if (statusFilter) {
      result = result.filter(q => q.status === statusFilter);
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(r =>
        (r.quote_no || '').toLowerCase().includes(q) ||
        (r.prospect_name || '').toLowerCase().includes(q) ||
        (r.phone || '').includes(q) ||
        (r.prepared_by || '').toLowerCase().includes(q)
      );
    }

    setFilteredQuotes(result);
  }, [quotes, timeFilter, statusFilter, searchQuery]);

  // Summary calculations
  const totalQuotes = quotes.length;
  const pendingQuotes = quotes.filter(q => ['Taslak', 'Müşteriye Sunuldu', 'Revize Bekliyor', 'Beklemede'].includes(q.status)).length;
  const approvedQuotes = quotes.filter(q => q.status === 'Onaylandı' || q.status === 'Satışa Dönüştürüldü').length;
  const rejectedQuotes = quotes.filter(q => q.status === 'Reddedildi').length;
  const expiredQuotes = quotes.filter(q => q.status === 'Süresi Doldu').length;
  const totalAmount = quotes.reduce((sum, q) => sum + (parseFloat(q.total_amount) || 0), 0);
  const approvedAmount = quotes.filter(q => q.status === 'Onaylandı' || q.status === 'Satışa Dönüştürüldü').reduce((sum, q) => sum + (parseFloat(q.total_amount) || 0), 0);
  const conversionRate = totalQuotes > 0 ? ((approvedQuotes / totalQuotes) * 100).toFixed(1) : '0';

  const statuses = ['Taslak', 'Müşteriye Sunuldu', 'Revize Bekliyor', 'Beklemede', 'Onaylandı', 'Reddedildi', 'Süresi Doldu', 'Satışa Dönüştürüldü', 'İptal Edildi'];

  const handleStatusChange = async (quoteId: string, newStatus: string) => {
    await updateQuoteStatus(quoteId, newStatus);
    loadQuotes();
  };

  const handleDelete = async (id: string) => {
    await deleteQuote(id);
    setDeleteConfirm(null);
    loadQuotes();
  };

  const formatCurrency = (val: number | string) => {
    const n = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(n)) return '0';
    return n.toLocaleString('tr-TR');
  };

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Teklifler</h1>
          <p className="text-sm text-foreground-600 mt-0.5">Teklifleri görüntüleyin, filtreleyin ve yönetin</p>
        </div>
        <button
          onClick={() => navigate('/quotes/new')}
          className="whitespace-nowrap px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer flex items-center gap-2 self-start"
        >
          <i className="ri-add-line"></i>
          Yeni Teklif
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Toplam Teklif', value: totalQuotes, icon: 'ri-file-list-3-line', color: 'bg-foreground-100', iconColor: 'text-foreground-600' },
          { label: 'Bekleyen', value: pendingQuotes, icon: 'ri-time-line', color: 'bg-secondary-100', iconColor: 'text-secondary-600' },
          { label: 'Onaylanan', value: approvedQuotes, icon: 'ri-check-double-line', color: 'bg-primary-100', iconColor: 'text-primary-600' },
          { label: 'Reddedilen', value: rejectedQuotes, icon: 'ri-close-circle-line', color: 'bg-red-100', iconColor: 'text-red-500' },
          { label: 'Süresi Geçen', value: expiredQuotes, icon: 'ri-alert-line', color: 'bg-foreground-200', iconColor: 'text-foreground-500' },
          { label: 'Toplam Tutar', value: `₺${formatCurrency(totalAmount)}`, icon: 'ri-money-dollar-circle-line', color: 'bg-accent-100', iconColor: 'text-accent-600' },
          { label: 'Onaylanan Tutar', value: `₺${formatCurrency(approvedAmount)}`, icon: 'ri-funds-line', color: 'bg-primary-100', iconColor: 'text-primary-600' },
          { label: 'Dönüşüm Oranı', value: `%${conversionRate}`, icon: 'ri-line-chart-line', color: 'bg-secondary-100', iconColor: 'text-secondary-600' },
        ].map((card, i) => (
          <div key={i} className={`${card.color} rounded-lg p-3 md:p-4 flex items-center gap-3`}>
            <div className={`w-10 h-10 md:w-11 md:h-11 flex-shrink-0 flex items-center justify-center rounded-lg bg-background-50`}>
              <i className={`${card.icon} ${card.iconColor} text-base md:text-lg`}></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-foreground-600 truncate">{card.label}</p>
              <p className="text-base md:text-lg font-bold text-foreground-950 truncate">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Time filters */}
        <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
          {TIME_FILTERS.map(tf => (
            <button
              key={tf.key}
              onClick={() => setTimeFilter(tf.key)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${timeFilter === tf.key ? 'bg-background-50 text-foreground-950' : 'text-foreground-600 hover:text-foreground-800'}`}
            >
              {tf.label}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-700 cursor-pointer"
        >
          <option value="">Tüm Durumlar</option>
          {statuses.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[400px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Teklif no, müşteri adı, telefon ara..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
          />
        </div>
      </div>

      {/* Table */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-4 text-sm">
          {error}
          <button onClick={loadQuotes} className="ml-3 underline cursor-pointer">Tekrar dene</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
          <p className="mt-2 text-sm">Yükleniyor...</p>
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="text-center py-20 text-foreground-500">
          <i className="ri-file-list-3-line text-4xl"></i>
          <p className="mt-2 text-sm">Henüz teklif bulunmuyor</p>
          <button onClick={() => navigate('/quotes/new')} className="mt-3 text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
            İlk teklifi oluştur
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-background-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-3 text-foreground-600 font-medium whitespace-nowrap">Teklif No</th>
                <th className="px-4 py-3 text-foreground-600 font-medium whitespace-nowrap">Müşteri Adayı</th>
                <th className="px-4 py-3 text-foreground-600 font-medium whitespace-nowrap">Telefon</th>
                <th className="px-4 py-3 text-foreground-600 font-medium whitespace-nowrap">Durum</th>
                <th className="px-4 py-3 text-foreground-600 font-medium whitespace-nowrap text-right">Tutar</th>
                <th className="px-4 py-3 text-foreground-600 font-medium whitespace-nowrap">Tarih</th>
                <th className="px-4 py-3 text-foreground-600 font-medium whitespace-nowrap">Geçerlilik</th>
                <th className="px-4 py-3 text-foreground-600 font-medium whitespace-nowrap">Hazırlayan</th>
                <th className="px-4 py-3 text-foreground-600 font-medium whitespace-nowrap text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {filteredQuotes.map(q => (
                <tr
                  key={q.id}
                  className="hover:bg-background-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/quotes/${q.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-foreground-900 whitespace-nowrap">{q.quote_no}</td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{q.prospect_name || '-'}</td>
                  <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{q.phone || '-'}</td>
                  <td className="px-4 py-3">
                    <Badge badgeClass={quoteStatus.badgeClass(q.status)} icon={quoteStatus.icon(q.status)}>{q.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-foreground-900 whitespace-nowrap">₺{formatCurrency(q.total_amount)}</td>
                  <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{q.quote_date ? new Date(q.quote_date).toLocaleDateString('tr-TR') : '-'}</td>
                  <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{q.valid_until ? new Date(q.valid_until).toLocaleDateString('tr-TR') : '-'}</td>
                  <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{q.prepared_by || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/quotes/${q.id}`)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer"
                        title="Görüntüle"
                      >
                        <i className="ri-eye-line text-sm"></i>
                      </button>
                      {!['Onaylandı', 'Satışa Dönüştürüldü', 'İptal Edildi', 'Reddedildi'].includes(q.status) && (
                        <button
                          onClick={() => setDeleteConfirm(q.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 cursor-pointer"
                          title="Sil"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 animate-fade-in">
          <div className="bg-background-50 rounded-xl p-6 max-w-sm w-full mx-4 shadow-sm">
            <h3 className="text-lg font-semibold text-foreground-950 mb-2">Teklifi Sil</h3>
            <p className="text-sm text-foreground-600 mb-5">Bu teklifi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="whitespace-nowrap px-4 py-2 rounded-lg bg-background-100 text-foreground-700 text-sm hover:bg-background-200 cursor-pointer"
              >
                İptal
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="whitespace-nowrap px-4 py-2 rounded-lg bg-red-500 text-background-50 text-sm hover:bg-red-600 cursor-pointer"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}